import { getUrl, remove, uploadData } from 'aws-amplify/storage';
import { createAdmissionsFeatureBundle } from '@triedge/admissions-ui/feature-bundle';
import type { AssistantFeatureTransport } from '@triedge/ai-assistant-ui/feature-bundle';
import { domainRouterClient } from '../api/domainRouterClient';
import { resolveCognitoProfile } from '../utils/cognito-profile';

type AdmissionsTransport = Parameters<typeof createAdmissionsFeatureBundle>[0]['transport'];
type AssistantTransportMethod<Name extends keyof AssistantFeatureTransport> =
  NonNullable<AssistantFeatureTransport[Name]>;

const queryAdmissions = ((name, args) =>
  domainRouterClient.query(name as never, args as never) as Promise<unknown>) as AdmissionsTransport['query'];

const mutateAdmissions = ((name, args) =>
  domainRouterClient.mutate(name as never, args as never) as Promise<unknown>) as AdmissionsTransport['mutate'];

const admissionsTransport = {
  query: queryAdmissions,
  mutate: mutateAdmissions,
  askSchoolAI: ((input) => domainRouterClient.mutate('askSchoolAI', {
    ...input,
    model: input.model ?? undefined,
  } as never)) as AssistantTransportMethod<'askSchoolAI'>,
  listAssistantCapabilities: ((input) =>
    domainRouterClient.query('listAssistantCapabilities', input as never)) as AssistantTransportMethod<'listAssistantCapabilities'>,
  previewAssistantAction: ((input) =>
    domainRouterClient.query('previewAssistantAction', input as never)) as AssistantTransportMethod<'previewAssistantAction'>,
  executeAssistantAction: ((input) =>
    domainRouterClient.mutate('executeAssistantAction', input as never)) as AssistantTransportMethod<'executeAssistantAction'>,
  getAssistantSessionState: ((input) =>
    domainRouterClient.query('getAssistantSessionState', input as never)) as AssistantTransportMethod<'getAssistantSessionState'>,
  listAssistantConversationSessions: ((input) =>
    domainRouterClient.query('listAssistantConversationSessions', input as never)) as AssistantTransportMethod<'listAssistantConversationSessions'>,
  getAssistantConversationSessionTimeline: ((input) =>
    domainRouterClient.query('getAssistantConversationSessionTimeline', input as never)) as AssistantTransportMethod<'getAssistantConversationSessionTimeline'>,
  startAssistantConversationSession: ((input) =>
    domainRouterClient.mutate('startAssistantConversationSession', input as never)) as AssistantTransportMethod<'startAssistantConversationSession'>,
  closeAssistantConversationSession: ((input) =>
    domainRouterClient.mutate('closeAssistantConversationSession', input as never)) as AssistantTransportMethod<'closeAssistantConversationSession'>,
  appendAssistantConversationMessages: ((input) =>
    domainRouterClient.mutate('appendAssistantConversationMessages', input as never)) as AssistantTransportMethod<'appendAssistantConversationMessages'>,
  listAssistantWorkItems: ((input) =>
    domainRouterClient.query('listAssistantWorkItems', { input } as never)) as AssistantTransportMethod<'listAssistantWorkItems'>,
  mutateAssistantRun: ((input) =>
    domainRouterClient.mutate('mutateAssistantRun', { input } as never)) as AssistantTransportMethod<'mutateAssistantRun'>,
  submitAssistantFeedback: ((input) =>
    domainRouterClient.mutate('submitAssistantFeedback', { input } as never)) as AssistantTransportMethod<'submitAssistantFeedback'>,
  getAssistantLearningStatus: ((input) =>
    domainRouterClient.query('getAssistantLearningStatus', input as never)) as AssistantTransportMethod<'getAssistantLearningStatus'>,
  getAssistantRuntimePolicy: (() =>
    domainRouterClient.query('getAssistantRuntimePolicy', {} as never)) as AssistantTransportMethod<'getAssistantRuntimePolicy'>,
  updateAssistantRuntimePolicy: ((input) =>
    domainRouterClient.mutate('updateAssistantRuntimePolicy', { input } as never)) as AssistantTransportMethod<'updateAssistantRuntimePolicy'>,
} satisfies AdmissionsTransport & AssistantFeatureTransport;

const admissionsStorage = {
  async resolveFileUrl(resourceId: string) {
    if (/^https?:\/\//i.test(resourceId)) {
      return resourceId;
    }
    const signed = await getUrl({ path: resourceId });
    return signed.url.toString();
  },
  async uploadFile(input: {
    applicationId: string;
    category: string;
    documentName: string;
    file: File;
    childApplicationIds?: string[];
  }) {
    const actorId = resolveCognitoProfile()?.userSub ?? 'staff';
    const storagePath = [
      'uploads',
      'admissions',
      input.applicationId,
      actorId,
      `${Date.now()}-${input.documentName.replace(/[^a-zA-Z0-9._-]+/g, '_')}`,
    ].join('/');

    await uploadData({
      path: storagePath,
      data: input.file,
      options: {
        contentType: input.file.type || 'application/octet-stream',
        metadata: {
          applicationId: input.applicationId,
          uploadedBy: actorId,
          documentName: input.documentName,
          category: input.category,
          childApplicationIds: (input.childApplicationIds ?? []).join(','),
        },
      },
    }).result;

    return {
      resourceId: storagePath,
      uploadedBy: actorId,
      fileName: input.file.name,
      contentType: input.file.type || 'application/octet-stream',
      sizeBytes: input.file.size,
    };
  },
  async removeFile(resourceId: string) {
    await remove({ path: resourceId });
  },
  resolveCurrentActorId() {
    return resolveCognitoProfile()?.userSub ?? 'staff';
  },
};

export const admissionsFeatureBundle = createAdmissionsFeatureBundle({
  transport: admissionsTransport,
  storage: admissionsStorage,
  routes: {
    staffBase: '/school/admissions',
    parentApplicationsPath: '/parent/portal/applications',
    parentIntakePath: '/parent/portal--new/applications',
  },
  reducerPath: 'admissionsFeatureApi',
});
